/** Section 3.7 - the `PlayerController` the adventure loop drives.
 *
 * Two contracts, and they are the whole file.
 *
 * **I9.** The only input is a `PlayerObservation`. Everything the planner reads
 * comes out of the shadow state built from it (`shadow.ts`), and every command
 * it may issue comes out of `enumerateLegalCommands` run against that shadow -
 * the same function a human's UI highlights from.
 *
 * **The abort.** `chooseCommand` must return its best command *so far* when the
 * signal fires, not throw and not hang. That is why the decision is built in
 * widening phases, cheapest first, with `best` updated after each: an abort
 * before any phase completes still yields a legal command, and an abort after
 * the cheap phases yields a decent one.
 */

import {
  SIDE_ATTACKER, SIDE_DEFENDER, TERRAINS, enumerateLegalCommands, hashString, isMoveFamily,
} from "@game/core";
import type {
  ContentRegistry, GameCommand, HeroState, InteractionResponse, LegalCommand,
  ObjectId, PlayerId, PlayerObservation,
} from "@game/core";
import { buildShadow, type Shadow } from "./shadow.js";
import {
  aimAtMouth, knownLinks, learnTransits, newPortalMemory, portalMouths,
} from "./portals.js";
import { chooseBattleCommand } from "./battle.js";
import {
  assessGuard, guardOf, newFightBudget, planAdventure, planDig, planSpells,
  planVisits, resourceShortage, willFight, type FightBudget,
} from "./adventure.js";
import { planTown } from "./town.js";
import { planGear } from "./gear.js";
import { armyStrength } from "./evaluate.js";
import {
  resolveDifficulty, type DifficultyId, type DifficultyProfile,
} from "./difficulty.js";
import {
  commandKey, compareCandidates, type Candidate, type PlayerController,
} from "./types.js";

export interface AiControllerOptions {
  readonly content: ContentRegistry;
  readonly difficulty?: DifficultyId | DifficultyProfile;
  /** Overrides the difficulty's own per-turn command cap. */
  readonly commandsPerTurn?: number;
}

export interface AiController extends PlayerController {
  readonly kind: "ai";
  readonly profile: DifficultyProfile;
  /** The scored options behind the last decision, best first. Debug only. */
  explain(): readonly Candidate[];
}

/**
 * Secondary skills, best first, by what a computer opponent can actually use.
 * Movement compounds over a whole game and nothing else does, so Logistics and
 * Pathfinding sit above the combat skills; Wisdom is high because a hero that
 * cannot learn level-three spells wastes every shrine it walks past.
 */
const SKILL_PREFERENCE: readonly string[] = Object.freeze([
  "logistics", "earth_magic", "wisdom", "offense", "armorer", "archery",
  "air_magic", "pathfinding", "leadership", "luck", "diplomacy", "estates",
  "intelligence", "sorcery", "water_magic", "fire_magic", "tactics",
  "artillery", "ballistics", "navigation", "scouting", "mysticism",
  "first_aid", "eagle_eye", "necromancy", "scholar",
]);

const skillRank = (id: string): number => {
  const i = SKILL_PREFERENCE.indexOf(id);
  return i < 0 ? SKILL_PREFERENCE.length : i;
};

/**
 * Where one subskill branch falls in a hero's own shuffle, 0..9999.
 *
 * Taking the first branch every time is stable and also means every Bonecaller
 * the computer ever fields builds the same hero: Dread Lord, then Bone Lord,
 * for the whole game and every game. The branches of one skill are close enough
 * in value that ranking them properly would need the hero's army and route, and
 * a ranking that is wrong in one fixed direction is worse than no ranking - so
 * this spreads them instead.
 *
 * A hash and not a random number, because I12 says the same seed replays to the
 * same game: the order is a pure function of the hero, the skill and the branch,
 * so a replay picks what the original picked, while two heroes of the same class
 * standing in the same game diverge. The AI has no RNG stream of its own and
 * must not reach into the kernel's (I9), which is the other reason.
 */
const branchRank = (hero: string, skill: string, branch: string): number =>
  Number.parseInt(hashString(`${hero}/${skill}/${branch}`).slice(0, 8), 16) % 10000;

// ------------------------------------------------------------------ prompts

function heroNear(sh: Shadow, x: number, y: number, z: number): HeroState | null {
  let best: HeroState | null = null;
  for (const h of sh.obs.heroes) {
    const d = h.detail;
    if (d === null || d.z !== z) continue;
    if (Math.abs(d.x - x) > 1 || Math.abs(d.y - y) > 1) continue;
    if (best === null || armyStrength(sh.content, sh.state.armies[d.army] ?? null)
      > armyStrength(sh.content, sh.state.armies[best.army] ?? null)) best = d;
  }
  return best;
}

/**
 * Answer the open prompt. Scored rather than hard-coded because the same
 * `RESPOND_TO_INTERACTION` command carries a level-up choice, a fight-or-flee
 * decision and a plain acknowledgement, and only the prompt tells them apart.
 */
/**
 * Take this fight, or run from it.
 *
 * Answered by the real battle kernel, on the same probe budget and through the
 * same `assessGuard` the movement scorer prices a guarded object with. It used
 * to be answered by a strength ratio against the kernel's own `threat` number,
 * and that was a second estimator in a codebase whose rule is that there is
 * one: the ratio accepts at about 1.27 times raw parity, which is a fight a
 * real battle loses, and the measured result was both starting heroes dead to
 * neutrals on day one of 36x36 seed 40595 with the game over on day three.
 *
 * The guard comes from the object's *definition* payload, which is content and
 * therefore public knowledge - the same channel `collectTargets` uses. The
 * instance payload the kernel materialises the guard from is hidden by
 * observation and is never read here. When there is no definition to build one
 * from, the kernel's threat number and `attackRatioFp` are still the fallback,
 * because an unanswerable prompt is a deadlocked turn.
 */
function fightOrFlee(
  sh: Shadow, p: DifficultyProfile, budget: FightBudget,
  objectId: ObjectId, threat: number,
): boolean {
  const o = sh.state.objects[objectId];
  const hero = o === undefined ? null : heroNear(sh, o.x, o.y, o.z);
  if (hero === null) return threat <= 0;
  const guard = o === undefined ? null : guardOf(sh, sh.content.mapObjects.get(o.def));
  if (guard !== null) {
    const terrain = TERRAINS[sh.obs.map.terrain[
      o!.z * sh.obs.map.width * sh.obs.map.height + o!.y * sh.obs.map.width + o!.x
    ] ?? 0] ?? "grass";
    return willFight(assessGuard(sh, p, budget, hero, guard, terrain));
  }
  const ours = armyStrength(sh.content, sh.state.armies[hero.army] ?? null);
  return threat <= 0 || ours * 1000 >= p.attackRatioFp * threat;
}

function planPrompt(
  sh: Shadow, p: DifficultyProfile, budget: FightBudget, legal: readonly LegalCommand[],
): Candidate[] {
  const pending = sh.obs.pending;
  if (pending === null) return [];
  const out: Candidate[] = [];

  const score = (r: InteractionResponse): number => {
    switch (pending.prompt.kind) {
      case "LEVEL_UP": {
        if (r.kind !== "OPTION") return 0;
        const choice = pending.prompt.choices[r.index];
        if (choice === undefined) return 0;
        return 1000 - skillRank(choice.replace(/^skill\./, ""));
      }
      case "PICK_SUBSKILL": {
        if (r.kind !== "OPTION") return 0;
        const choice = pending.prompt.choices[r.index];
        if (choice === undefined) return 0;
        return branchRank(
          pending.prompt.heroId as string,
          pending.prompt.skill as string,
          choice as string);
      }
      case "PICK_SPELL": {
        if (r.kind !== "OPTION") return 0;
        const id = pending.prompt.choices[r.index];
        const def = id === undefined ? undefined : sh.content.spells.get(id);
        return def === undefined ? 1 : def.level * 100;
      }
      case "FIGHT_OR_FLEE": {
        const take = fightOrFlee(sh, p, budget, pending.prompt.objectId, pending.prompt.threat);
        if (r.kind === "OK") return take ? 100 : 1;
        return take ? 1 : 100;
      }
      case "CHOOSE": {
        if (r.kind !== "OPTION") return 0;
        // A portal asks which mouth to come out of, and the options are object
        // ids rather than a gold-or-experience pair. The one worth taking is the
        // one on the other level - that is the whole reason a subterranean gate
        // was walked into - and an exit the fog has never shown us is one we
        // cannot judge, so it sorts below one we can.
        if (pending.prompt.text === "portal") {
          const id = pending.prompt.options[r.index];
          if (id === undefined) return 0;
          const exit = sh.state.objects[id as ObjectId];
          if (exit === undefined) return 20;
          const here = sh.state.objects[
            (pending.resumeToken.data["object"] ?? "") as ObjectId
          ];
          return here !== undefined && exit.z !== here.z ? 90 : 30;
        }
        // Treasure offers gold at 0 and experience at 1. Gold while the treasury
        // is thin, experience once building is no longer the constraint.
        if (r.index === 1) return (sh.obs.resources?.gold ?? 0) >= 10000 ? 60 : 40;
        return (sh.obs.resources?.gold ?? 0) >= 10000 ? 40 : 60;
      }
      case "CONFIRM":
      case "MESSAGE":
        return r.kind === "OK" ? 100 : 1;
    }
  };

  for (const c of legal) {
    if (c.type !== "RESPOND_TO_INTERACTION") continue;
    out.push({ command: c, score: 1_000_000 + score(c.response), why: "prompt" });
  }
  out.sort((a, b) => b.score - a.score);
  return out;
}

function planBattle(sh: Shadow, p: DifficultyProfile): Candidate[] {
  const b = sh.obs.battle;
  if (b === null) return [];
  const side = b.heroes[SIDE_ATTACKER].owner === sh.player ? SIDE_ATTACKER
    : b.heroes[SIDE_DEFENDER].owner === sh.player ? SIDE_DEFENDER : null;
  if (side === null) return [];
  const cmd = chooseBattleCommand(b, side as 0 | 1, sh.content, p);
  if (cmd === null) return [];
  return [{ command: { type: "BATTLE", cmd }, score: 1_000_000, why: "battle" }];
}

// ----------------------------------------------------------------- fallback

/**
 * A legal command found without planning, for the abort path and for the case
 * where every plan comes back empty. It costs one enumeration and never fails:
 * `END_TURN` is legal for the active player whenever nothing else is open.
 */
function fallbackFrom(player: PlayerId, legal: readonly LegalCommand[]): GameCommand {
  for (const c of legal) if (c.type === "RESPOND_TO_INTERACTION") return c;
  for (const c of legal) if (c.type === "BATTLE") return c;
  for (const c of legal) if (c.type === "END_TURN") return c;
  for (const c of legal) if (!isMoveFamily(c)) return c;
  return { type: "END_TURN", player };
}

// --------------------------------------------------------------- controller

export function createAiController(opts: AiControllerOptions): AiController {
  const profile = resolveDifficulty(opts.difficulty);
  const cap = opts.commandsPerTurn ?? profile.commandsPerTurn;
  const content = opts.content;

  let turnKey = "";
  let issued = new Set<string>();
  let spent = 0;
  let budget = newFightBudget(profile);
  let lastPlan: readonly Candidate[] = [];
  // Where this kingdom's gates come out, learned by walking through them. It
  // lives on the controller because it is knowledge the *player* accumulates
  // across turns, the same shape as the fog memory the observation carries, and
  // because a controller is per player and lasts the whole game.
  const portals = newPortalMemory();

  /** RECRUIT is the one command that is meant to repeat with identical arguments. */
  const repeatable = (c: GameCommand): boolean => c.type === "RECRUIT";

  const chooseCommand = async (
    obs: PlayerObservation, signal: AbortSignal,
  ): Promise<GameCommand> => {
    if (obs.viewer === "spectator" || obs.self === null) {
      throw new Error("the AI controller was handed a spectator observation");
    }

    const key = `${obs.viewer}:${obs.turn.month}:${obs.turn.week}:${obs.turn.day}`;
    if (key !== turnKey) {
      turnKey = key;
      issued = new Set<string>();
      spent = 0;
      // The probe allowance and its answers are turn-scoped; see `FightBudget`.
      budget = newFightBudget(profile);
    }

    // Answered before anything is planned: a hero who is not where yesterday's
    // route left him has been through a gate, and that is the one way this
    // planner ever learns where a gate goes.
    learnTransits(portals, obs, content);
    const sh = buildShadow(obs, content, profile.useMemory, knownLinks(portals));
    const legal = enumerateLegalCommands(sh.state, sh.player, content);
    const fallback = fallbackFrom(sh.player, legal);
    lastPlan = [];
    if (legal.length === 0) return fallback;

    // A prompt and a battle are not choices among many; while either is open
    // they are the only thing the kernel will accept.
    const forced = [...planPrompt(sh, profile, budget, legal), ...planBattle(sh, profile)];
    if (forced.length > 0) {
      lastPlan = forced;
      return forced[0]!.command;
    }

    if (spent >= cap) return endTurn(sh.player);

    const pool: Candidate[] = [];

    const absorb = (phase: readonly Candidate[]): void => {
      for (const c of phase) {
        if (c.score <= 0) continue;
        if (!repeatable(c.command) && issued.has(commandKey(c.command))) continue;
        pool.push(c);
      }
    };

    // What the AI is short of is a property of the position, not of a phase, so
    // it is computed once and handed to both consumers.
    const short = resourceShortage(sh);

    // Phase order is cheapest first, so an abort always leaves something behind.
    // Before everything, and cheapest of all: a dig is one command, it is only
    // ever offered on the one cell in the map that pays for it, and the hero
    // standing there has nothing better to do with the day.
    if (!signal.aborted) absorb(planDig(sh, legal));
    // Free actions on things the kingdom already owns: wear it, and build the
    // set if the set is there.
    if (!signal.aborted) absorb(planGear(sh, legal));
    if (!signal.aborted) absorb(planTown(sh, profile, legal));
    if (!signal.aborted) absorb(planVisits(sh, legal, short));
    if (!signal.aborted) absorb(planSpells(sh, profile, legal, budget, short));
    if (!signal.aborted) absorb(planAdventure(sh, profile, legal, budget, short, signal));

    // The pool is everything the phases that did finish produced, so this is
    // literally "the best command so far" whether or not the signal fired.
    pool.sort(compareCandidates);
    lastPlan = pool;

    const pick = pool[0];
    if (pick === undefined) return endTurn(sh.player);
    spent++;
    issued.add(commandKey(pick.command));
    noteRoute(sh, pick.command);
    return pick.command;
  };

  /**
   * Remember that this walk ends by stepping onto a portal mouth, so that
   * tomorrow's observation - which will find the hero somewhere else entirely -
   * can be read as the jump it was.
   */
  const noteRoute = (sh: Shadow, command: GameCommand): void => {
    if (command.type !== "MOVE_HERO") return;
    const last = command.path[command.path.length - 1];
    if (last === undefined) return;
    aimAtMouth(portals, command.hero, last, portalMouths(sh.obs, content));
  };

  const endTurn = (player: PlayerId): GameCommand => {
    spent = cap;
    return { type: "END_TURN", player };
  };

  return {
    kind: "ai",
    profile,
    explain: () => lastPlan,
    chooseCommand,
  };
}
